import { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText } from "@mui/material";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { samePortValidation } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { type PortServicesOutletContext } from "@features/vessel-call/vessel-call-status/PortServices/PortServicesContainer";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { validateShipToShipVesselCall } from "@services/portServicesApi";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

const VESSEL_CALL_SERVICE = "bff-vessel-call";
const OTHER_VESSEL_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_call",
  screen: "Ship To Ship Port Service",
  action: "validate_other_vessel_call",
  fromField: "otherVesselImoNumber",
  toField: "otherVesselName",
  entityType: "vessel_call",
};

const ShipToShipFields = ({ langBase }: { langBase: string }) => {
  const { t } = useTranslation();
  const { formData, updateFormData, currentVesselImoNumber, selectedPortCode } =
    useOutletContext<PortServicesOutletContext>();
  const imoErrorMessage = formData.formState.errors.otherVesselImoNumber?.message;
  const isOtherVesselImoTouched = formData.formState.touchedFields.otherVesselImoNumber;
  const [imoValidationMessages, setImoValidationMessages] = useState<string[]>([]);
  const otherVesselImoNumber = formData.watch("otherVesselImoNumber");
  const previousImoRef = useRef(otherVesselImoNumber);
  const validationRequestRef = useRef(0);
  const normalizedCurrentVesselImoNumber = currentVesselImoNumber?.trim();
  const isSameAsCurrentVesselImo = Boolean(
    normalizedCurrentVesselImoNumber &&
      otherVesselImoNumber?.trim() === normalizedCurrentVesselImoNumber &&
      isOtherVesselImoTouched,
  );

  const setOtherVesselImoError = useCallback(
    (messages: string[]) => {
      setImoValidationMessages(messages);
      formData.setError("otherVesselImoNumber", {
        type: "manual",
        message: messages.join(" "),
      });
    },
    [formData],
  );

  const resetOtherVesselDetails = useCallback(() => {
    const emptyFields: Array<[keyof PortServicesFormData, string]> = [
      ["otherVesselId", ""],
      ["otherVesselName", ""],
      ["otherVesselCallId", ""],
      ["otherVesselCallNumber", ""],
    ];

    emptyFields.forEach(([fieldName, value]) => {
      formData.setValue(fieldName, value as never, { shouldDirty: true, shouldValidate: false });
      updateFormData(fieldName, value);
    });
  }, [formData, updateFormData]);

  useEffect(() => {
    if (previousImoRef.current === otherVesselImoNumber) {
      return;
    }

    previousImoRef.current = otherVesselImoNumber;
    setImoValidationMessages([]);
    resetOtherVesselDetails();
    formData.clearErrors("otherVesselImoNumber");
  }, [formData, otherVesselImoNumber, resetOtherVesselDetails]);

  const validateOtherVesselImo = async () => {
    const rawImo = String(formData.getValues("otherVesselImoNumber") ?? "").trim();
    const validationRequestId = validationRequestRef.current + 1;

    validationRequestRef.current = validationRequestId;
    setImoValidationMessages([]);
    resetOtherVesselDetails();

    if (!rawImo) {
      setOtherVesselImoError([
        t("form-validation.required", {
          label: t(`${langBase}.fields.other-vessel-imo-number`),
        }),
      ]);
      return;
    }

    if (!/^\d+$/.test(rawImo)) {
      setOtherVesselImoError([
        t("form-validation.invalid-number", {
          label: t(`${langBase}.fields.other-vessel-imo-number`),
        }),
      ]);
      return;
    }

    if (normalizedCurrentVesselImoNumber && rawImo === normalizedCurrentVesselImoNumber) {
      setOtherVesselImoError([t("form-validation.cannot-be-same-imo")]);
      return;
    }

    try {
      const shipToShipValidation = await runFieldNavigation(
        OTHER_VESSEL_FIELD_NAVIGATION_CONTEXT,
        () => validateShipToShipVesselCall(VESSEL_CALL_SERVICE, rawImo),
      );

      if (
        validationRequestRef.current !== validationRequestId ||
        String(formData.getValues("otherVesselImoNumber") ?? "").trim() !== rawImo
      ) {
        return;
      }

      if (!samePortValidation(shipToShipValidation.port, selectedPortCode)) {
        setOtherVesselImoError([t("form-validation.imo-does-not-exist")]);
        return;
      }

      const prioritizedValidationMessage = !shipToShipValidation.isExistingVessel
        ? t(`${langBase}.fields.other-vessel-not-registered`)
        : !shipToShipValidation.isValidStatus
          ? t(`${langBase}.fields.other-vessel-no-active-call`)
          : !shipToShipValidation.isValidCertificate
            ? t(`${langBase}.fields.other-vessel-invalid-certificates`)
            : null;

      if (prioritizedValidationMessage) {
        setOtherVesselImoError([prioritizedValidationMessage]);
        return;
      }

      if (!shipToShipValidation.vesselCallId) {
        setOtherVesselImoError([t(`${langBase}.fields.other-vessel-validation-failed`)]);
        return;
      }

      setImoValidationMessages([]);
      formData.clearErrors("otherVesselImoNumber");
      const nextValues: Array<[keyof PortServicesFormData, string]> = [
        ["otherVesselImoNumber", rawImo],
        ["otherVesselId", ""],
        ["otherVesselName", shipToShipValidation.vesselName ?? ""],
        ["otherVesselCallId", shipToShipValidation.vesselCallId],
        [
          "otherVesselCallNumber",
          shipToShipValidation.vesselCallNumber ??
            shipToShipValidation.vesselCallNo ??
            shipToShipValidation.vcn ??
            "",
        ],
      ];

      nextValues.forEach(([fieldName, value]) => {
        formData.setValue(fieldName, value as never, { shouldDirty: true, shouldValidate: false });
        updateFormData(fieldName, value);
      });
    } catch {
      if (validationRequestRef.current === validationRequestId) {
        setOtherVesselImoError([t(`${langBase}.fields.other-vessel-validation-failed`)]);
      }
    }
  };

  const displayedImoMessages = isSameAsCurrentVesselImo
    ? [t("form-validation.cannot-be-same-imo")]
    : imoValidationMessages.length > 0
      ? imoValidationMessages
      : imoErrorMessage
        ? [String(imoErrorMessage)]
        : [];

  return (
    <FormProvider {...formData}>
      <div className="flex flex-col gap-6" data-testid="ship-to-ship-fields">
        <div className="grid grid-cols-2">
          <Controller
            name="otherVesselImoNumber"
            control={formData.control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <CustomFormLabel>{t(`${langBase}.fields.other-vessel-imo-number`)}</CustomFormLabel>
                <Input
                  {...field}
                  type="number"
                  testId="ship-to-ship-other-vessel-imo-number"
                  error={displayedImoMessages.length > 0}
                  value={field.value ?? ""}
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                  onKeyDown={(event) => {
                    const invalidKeys = ["e", "E", "+", "-", "."];
                    if (invalidKeys.includes(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    if (!/^\d*$/.test(nextValue) || nextValue.length > 8) {
                      return;
                    }

                    field.onChange(nextValue);
                    updateFormData("otherVesselImoNumber", nextValue);
                  }}
                  onBlur={(event) => {
                    const trimmedValue = event.target.value.trim();
                    if (trimmedValue !== field.value) {
                      field.onChange(trimmedValue);
                      updateFormData("otherVesselImoNumber", trimmedValue);
                    }

                    field.onBlur();
                    void validateOtherVesselImo();
                  }}
                />
                {displayedImoMessages.map((message) => (
                  <FormHelperText key={message} error>
                    {message}
                  </FormHelperText>
                ))}
              </div>
            )}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default ShipToShipFields;
